import React, { useState, useEffect, useRef } from 'react';
import GlobalNav from '../components/GlobalNav';
import AudioRecorder from '../components/AudioRecorder';
import Profile from './Epk';
import Registry from '../components/Registry';
import Calendar from '../components/Calendar';
import HubRegistrations from '../components/HubRegistrations';
import AudioMix from '../components/AudioMix';
import Voicemail from '../components/Voicemail';
import AddAlbum from '../components/AddAlbum';
import AddEpisode from '../components/AddEpisode';
import AddPost from '../components/AddPost';
import AdminEventForm from '../components/AdminEventForm';
import Projects from '../pages/Projects';
import TaskBoard from '../pages/TaskBoard';
import './Hub.css';

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function formatTimeAgo(timestamp) {
  const now = new Date();
  const d = new Date(timestamp);
  const ms = now - d;
  const mins = Math.floor(ms / 60000);
  const hours = Math.floor(ms / 3600000);
  const days = Math.floor(ms / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function truncateHTML(html, max = 150) {
  const div = document.createElement('div');
  div.innerHTML = html;
  const text = div.textContent || div.innerText || '';
  return text.length > max ? text.substring(0, max) + '…' : text;
}

const ACTIVITY_COLORS = {
  Article: 'var(--mihu-navy, #2F3A78)',
  Video: '#d32f2f',
  Episode: '#d32f2f',
  Audio: 'var(--mihu-teal, #2A8F85)',
  Image: 'var(--mihu-orange, #D16A4B)',
  Event: 'var(--mihu-teal, #2A8F85)',
  Member: 'var(--mihu-navy, #2F3A78)',
  Like: 'var(--mihu-orange, #D16A4B)',
};

// Sidebar links now drive in-place view switching (currentView) instead of
// navigating to separate routes — matches the reference file's pattern.
const SIDEBAR_LINKS = [
  { view: 'home', label: 'Home', icon: '⌂' },
  { view: 'profile', label: 'Profile', icon: '◯' },
  { view: 'registry', label: 'Artists', icon: '♪' },
  { view: 'calendar', label: 'Calendar', icon: '▦' },
];

const ADMIN_LINKS = [
  { view: 'registrations', label: 'Approve registrations' },
  { view: 'record', label: 'Record audio' },
  { view: 'audio', label: 'Mix audio' },
  { view: 'voicemail', label: 'Check messages' },
  { view: 'newAlbum', label: 'Add album' },
  { view: 'newEpisode', label: 'Add episode' },
  { view: 'post', label: 'Post blog' },
  { view: 'calendar', label: 'Event calendar' },
  // No backend yet — see comment on the newEvent view below.
  { view: 'newEvent', label: 'Add event' },
  // Real /api/tasks-backed view — same data source as Task board will
  // eventually use, but this one already works end-to-end.

  // { view: 'projects', label: 'Projects' },
  
  // Backed by routes/internal.js's GET/POST /api/tasks — TaskBoard.jsx
  // itself still needs to be sent so this view has real content instead
  // of the placeholder below.
  { view: 'taskboard', label: 'Task board' },
];

const VIEW_TITLES = {
  home: null, // uses "Welcome, {firstname}" instead
  profile: 'Profile',
  registry: 'Artists',
  calendar: 'Calendar',
  registrations: 'Registrations',
  record: 'Record audio',
  audio: 'Mix audio',
  voicemail: 'Messages',
  newAlbum: 'Add album',
  newEpisode: 'Add episode',
  post: 'Post blog',
  taskboard: 'Task board',
  newEvent: 'Add event',
  projects: 'Projects',
};

// Placeholder for any view whose real (non-MUI) component hasn't been
// rebuilt/sent yet. Keeps the shell fully functional — clicking a sidebar
// link always shows something coherent — without silently importing an
// old MUI component that could reintroduce dependency issues (like the
// Twilio/stream error from a transitive import) into a page that's
// otherwise MUI-free.
function ComingSoon({ label }) {
  return (
    <div className="hub-coming-soon">
      <div className="hub-coming-soon-icon">🚧</div>
      <h2>{label}</h2>
      <p>This view hasn't been rebuilt for the new theme yet — send its current source and I'll port it in.</p>
    </div>
  );
}

// ─── COMPONENT ───────────────────────────────────────────────────────────────

export default function Hub() {
  const [user, setUser] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [audioSegments, setAudioSegments] = useState([]);
  const [audioFiles, setAudioFiles] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentView, setCurrentView] = useState('home');

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) {
      window.location.href = '/signin?redirect=/hub';
      return;
    }
    try {
      const parsed = JSON.parse(stored);
      setUser(parsed);
      fetchHubData();
      fetchActivities();
    } catch (e) {
      localStorage.removeItem('user');
      window.location.href = '/signin';
    }
  }, []);

  async function fetchHubData() {
    try {
      const [blRes, auRes] = await Promise.all([
        fetch('/api/blogs'),
        fetch('/api/audio'),
      ]);
      const blData = await blRes.json();
      const auData = await auRes.json();

      const allBlogs = blData.blogs || [];
      setAudioSegments(allBlogs.filter((b) => b.audioURL));
      setBlogs(allBlogs.filter((b) => !b.audioURL));
      setAudioFiles(auData.audio || []);
    } catch (e) {
      console.error('Hub data error:', e);
    } finally {
      setLoading(false);
    }
  }

  async function fetchActivities() {
    try {
      const res = await fetch('/api/activity?limit=10');
      const data = await res.json();
      if (data.success) setActivities(data.activities || []);
    } catch (e) {
      console.error('Activity error:', e);
    }
  }

  function handleLogout() {
    localStorage.removeItem('user');
    window.location.href = '/users/logout';
  }

  // In-place switch, matching the reference file's nav() — closes the
  // mobile sidebar too, same as before.
  function nav(view) {
    setCurrentView(view);
    setSidebarOpen(false);
  }

  if (loading || !user) {
    return (
      <div className="hub-loading">
        <div className="hub-loading-spinner" />
        <p>Loading…</p>
      </div>
    );
  }

  const topbarTitle = VIEW_TITLES[currentView] || `Welcome, ${user.firstname}`;

  return (
    <div className="hub">
      <GlobalNav activeHref="/hub" />

      <div className="hub-shell">

        {/* ── Sidebar ── */}
        <aside className={"hub-sidebar" + (sidebarOpen ? " open" : "")}>
          <div className="hub-sidebar-logo">
            <a href="/"><img src="/images/wsl-logo-trans.png" alt="MIHU" /></a>
          </div>

          <nav className="hub-sidebar-nav">
            {SIDEBAR_LINKS.map((l) => (
              <button
                key={l.view}
                className={"hub-sidebar-link" + (currentView === l.view ? " active" : "")}
                onClick={() => nav(l.view)}
              >
                <span className="hub-sidebar-icon">{l.icon}</span>
                {l.label}
              </button>
            ))}
          </nav>

          {user.admin && (
            <div className="hub-sidebar-admin">
              <div className="hub-sidebar-section-label">Admin</div>
              {ADMIN_LINKS.map((l) => (
                <button
                  key={l.view}
                  className={"hub-sidebar-link hub-sidebar-link--admin" + (currentView === l.view ? " active" : "")}
                  onClick={() => nav(l.view)}
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}

          <div className="hub-sidebar-search">
            <input type="text" placeholder="Search…" className="hub-sidebar-search-input" />
          </div>

          <div className="hub-sidebar-footer">
            <button className="hub-sidebar-user" onClick={() => nav('profile')}>
              <div className="hub-sidebar-avatar">
                {user.photo
                  ? <img src={user.photo} alt={user.firstname} />
                  : <span>{user.firstname?.charAt(0)}</span>}
              </div>
              <div>
                <div className="hub-sidebar-name">{user.firstname} {user.lastname}</div>
                <div className="hub-sidebar-role">{user.roles || user.role || 'Member'}</div>
              </div>
            </button>
            <button className="hub-sidebar-logout" onClick={handleLogout}>Log out</button>
          </div>
        </aside>

        {sidebarOpen && (
          <div className="hub-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
        )}

        {/* ── Main ── */}
        <div className="hub-main">

          {/* <header className="hub-topbar">
            <button className="hub-burger" aria-label="Toggle sidebar" onClick={() => setSidebarOpen((v) => !v)}>
              <span /><span /><span />
            </button>
            <h1 className="hub-topbar-title">{topbarTitle}</h1>
            <button className="hub-topbar-avatar" onClick={() => nav('profile')} title="View profile">
              {user.photo
                ? <img src={user.photo} alt={user.firstname} />
                : <span>{user.firstname?.charAt(0)}</span>}
            </button>
          </header> */}

          <div className="hub-content">

            {/* ── In-place views ── */}
            {currentView === 'profile' && <Profile embedded />}
            {currentView === 'registry' && <Registry />}
            {currentView === 'calendar' && <Calendar />}
            {currentView === 'registrations' && <HubRegistrations />}
            {currentView === 'audio' && <AudioMix />}
            {currentView === 'voicemail' && <Voicemail />}
            {currentView === 'newAlbum' && <AddAlbum />}
            {currentView === 'newEpisode' && <AddEpisode />}
            {currentView === 'post' && <AddPost />}
            {/* No POST/PUT /admin/events route exists anywhere in this
                codebase yet, and the real `events` table (id, title,
                description, date, day, time, link, sponsor, city,
                imagepath) has no columns matching this form's fields
                (startsAt, location, capacity, rsvpEnabled). Submitting
                right now will hit the form's own error state every time —
                needs a migration + real route before this actually saves
                anything. Also unverified: whether FormInputs.js actually
                exports a `TextInput` component — I've only seen
                `InlineForm` from that file in this conversation. */}
            {currentView === 'newEvent' && <AdminEventForm />}
            {currentView === 'projects' && <Projects />}
            {/* TaskBoard.jsx exists but runs entirely on local mock data
                (GRANT_SEED, PARTNER_SEED, CONTACT_SEED, SEED) — none of it
                actually calls /api/tasks, /api/partnerships, /api/contacts,
                or /api/proposals despite those endpoints existing in
                routes/internal.js. It also imports a ./Projects component
                I don't have. Wiring it to the real backend is a separate,
                larger piece of work than the style port done here. */}
            {currentView === 'taskboard' && <TaskBoard />}

            {currentView === 'record' && (
              <section className="hub-section">
                <div className="hub-section-header">
                  <h2 className="hub-section-title">Record audio</h2>
                </div>
                <AudioRecorder />
              </section>
            )}

            {/* ── Home ── */}
            {currentView === 'home' && (
              <>
                <div className="hub-hero" style={{ backgroundImage: "url('/images/MusicIsHealing-Banner.png')" }}>
                  <div className="hub-hero-overlay" />
                  <div className="hub-hero-content">
                    <h2 className="hub-hero-title">Welcome to the Word. Sound. Life. HUB</h2>
                    <p className="hub-hero-sub">
                      A space where artists, performers &amp; mediamakers innovate, collaborate
                      and cross-pollinate to advance unity through music.
                    </p>
                  </div>
                </div>

                {activities.length > 0 && (
                  <section className="hub-section">
                    <div className="hub-section-header">
                      <h2 className="hub-section-title">Recent Activity</h2>
                    </div>
                    <div className="hub-activity-list">
                      {activities.map((a, i) => (
                        <div key={a._id || i} className="hub-activity-row">
                          <div className="hub-activity-avatar">
                            {a.avatar
                              ? <img src={a.avatar} alt={a.displayName} />
                              : <span>{a.displayName?.[0] || '?'}</span>}
                          </div>
                          <div className="hub-activity-body">
                            <span className="hub-activity-name">{a.displayName || a.user}</span>
                            {' '}<span className="hub-activity-desc">{a.description}</span>
                            {' '}
                            <span className="hub-activity-type" style={{ color: ACTIVITY_COLORS[a.type] || 'var(--mihu-slate)' }}>
                              {a.type}
                            </span>
                          </div>
                          <span className="hub-activity-time">{formatTimeAgo(a.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                  {user.admin && (audioSegments.length > 0 || audioFiles.length > 0) && (
                    <section className="hub-section">
                    <div className="hub-section-header">
                      <h2 className="hub-section-title">Audio Library</h2>
                    </div>
                    <div className="hub-audio-list">
                      {audioSegments.map((seg) => (
                        <div key={seg._id} className="hub-audio-row">
                          <div className="hub-audio-play">▶</div>
                          <div className="hub-audio-info">
                            <div className="hub-audio-title" onClick={() => window.location.href = `/blog#${seg._id}`}>
                              {seg.title || formatDate(seg.date)}
                            </div>
                            <div className="hub-audio-desc">{truncateHTML(seg.description, 100)}</div>
                          </div>
                          {seg.audioURL && (
                            <audio controls className="hub-audio-player">
                              <source src={seg.audioURL} type="audio/mpeg" />
                            </audio>
                          )}
                        </div>
                      ))}
                      {audioFiles.map((audio, idx) => (
                        <div key={`af-${idx}`} className="hub-audio-row">
                          <div className="hub-audio-play">▶</div>
                          <div className="hub-audio-info">
                            <div className="hub-audio-title">{audio.name?.replace('.mp3', '')}</div>
                            <div className="hub-audio-desc">{audio.source === 'github' ? 'GitHub Drive' : 'Local'}</div>
                          </div>
                          <audio controls className="hub-audio-player">
                            <source src={audio.path} type="audio/mpeg" />
                          </audio>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {user.admin && (
                  <section className="hub-section">
                    <div className="hub-section-header">
                      <h2 className="hub-section-title">Audio Recorder</h2>
                    </div>
                    <AudioRecorder />
                  </section>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}